import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { FACTIONS } from "@/data";
import { useForceStore } from "@/store/forceStore";
import type { FactionId } from "@/types/game-data";

interface NewRosterDialogProps {
	onClose: () => void;
}

export function NewRosterDialog({ onClose }: NewRosterDialogProps) {
	const createRoster = useForceStore((s) => s.createRoster);
	const rosters = useForceStore((s) => s.rosters);

	const [selectedFaction, setSelectedFaction] = useState<FactionId>("taskforce");
	const [name, setName] = useState("");
	const [maxPoints, setMaxPoints] = useState(2000);

	const trimmedName = name.trim();
	const isEmpty = trimmedName.length === 0;
	const isDuplicate = useMemo(
		() =>
			rosters.some(
				(r) => r.name.trim().toLowerCase() === trimmedName.toLowerCase(),
			),
		[rosters, trimmedName],
	);
	const isValid = !isEmpty && !isDuplicate;

	function handleCreate() {
		if (!isValid) return;
		createRoster(selectedFaction, trimmedName, maxPoints);
		onClose();
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Backdrop */}
			<div
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
				onKeyDown={(e) => {
					if (e.key === "Escape") onClose();
				}}
			/>

			{/* Dialog */}
			<div className="relative bg-bg-secondary border border-border-default rounded-lg shadow-lg p-6 w-full max-w-lg mx-4">
				<h2 className="text-lg font-bold text-text-primary mb-4">New Force</h2>

				{/* Force name */}
				<div className="mb-4">
					<label className="block text-xs text-text-muted uppercase tracking-wider mb-1.5">
						Force Name <span className="text-accent-danger">*</span>
					</label>
					<input
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && isValid) handleCreate();
						}}
						className={`w-full bg-bg-tertiary text-text-primary border rounded-md px-3 py-2 text-sm outline-none transition-colors ${
							name.length > 0 && !isValid
								? "border-accent-danger focus:border-accent-danger"
								: "border-border-default focus:border-accent-primary"
						}`}
						placeholder="Enter a unique force name..."
						autoFocus
					/>
					{name.length > 0 && isDuplicate && (
						<p className="text-xs text-accent-danger mt-1">
							A force with this name already exists
						</p>
					)}
				</div>

				{/* Budget */}
				<div className="mb-4">
					<label className="block text-xs text-text-muted uppercase tracking-wider mb-1.5">
						Point Budget
					</label>
					<input
						type="number"
						value={maxPoints}
						onChange={(e) => setMaxPoints(Number(e.target.value))}
						className="w-full bg-bg-tertiary text-text-primary border border-border-default rounded-md px-3 py-2 text-sm outline-none focus:border-accent-primary font-mono"
						min={0}
						step={100}
					/>
				</div>

				{/* Faction selector */}
				<div className="mb-6">
					<label className="block text-xs text-text-muted uppercase tracking-wider mb-1.5">
						Faction
					</label>
					<div className="flex flex-col gap-1.5 max-h-60 overflow-y-auto">
						{FACTIONS.map((f) => {
							const isSelected = selectedFaction === f.id;
							return (
								<button
									key={f.id}
									type="button"
									onClick={() => setSelectedFaction(f.id)}
									className={`text-left px-3 py-2.5 rounded-md text-sm transition-all cursor-pointer ${
										isSelected
											? "bg-accent-primary/10 text-accent-primary border border-accent-primary/30"
											: "text-text-secondary hover:bg-bg-tertiary border border-transparent"
									}`}
								>
									<div className="font-medium">{f.name}</div>
									{isSelected && (
										<div className="text-xs text-text-muted mt-1 leading-relaxed">
											{f.description}
										</div>
									)}
								</button>
							);
						})}
					</div>
				</div>

				{/* Actions */}
				<div className="flex justify-end gap-3">
					<Button variant="secondary" size="md" onClick={onClose}>
						Cancel
					</Button>
					<Button variant="primary" size="md" onClick={handleCreate} disabled={!isValid}>
						Create Force
					</Button>
				</div>
			</div>
		</div>
	);
}
